import React, { useState } from 'react';
import { 
  PackagePlus, 
  Users, 
  UserCheck,
  Store, 
  MessageSquare, 
  Megaphone, 
  CreditCard, 
  Upload, 
  HardDrive, 
  ChevronUp, 
  ChevronDown, 
  Shield, 
  Box, 
  Truck, 
  X,
  Sparkles,
  Sliders,
  ChevronRight,
  DollarSign,
  BarChart3,
  Mail,
  Server,
  Inbox,
  Search,
  Ticket,
  FileSpreadsheet,
  Database,
  TrendingUp
} from 'lucide-react';

interface AdminSidebarProps {
  isAdmin: boolean;
  isLoggedIn: boolean;
  onOpenAdminEntry?: () => void;
  onOpenPackageExcelImport?: () => void;
  onOpenClientImportModal?: () => void;
  onOpenAddStoreProductModal?: () => void;
  onOpenCouponsModal?: () => void;
  onOpenExcelProductImportModal?: () => void;
  onOpenWhatsAppModal?: () => void;
  onOpenGoogleDriveModal?: () => void;
  onOpenLogoModal?: () => void;
  onOpenPaymentMethodsModal?: () => void;
  onOpenExchangeRateModal?: () => void;
  onOpenHomePopupModal?: () => void;
  onOpenEmailMarketingModal?: () => void;
  onOpenSmtpConfigModal?: () => void;
  onOpenSearchConsoleModal?: () => void;
  onOpenOnamaeDbModal?: () => void;
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  pendingShipmentsCount?: number;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  isAdmin,
  isLoggedIn,
  onOpenAdminEntry,
  onOpenPackageExcelImport,
  onOpenClientImportModal,
  onOpenAddStoreProductModal,
  onOpenCouponsModal,
  onOpenExcelProductImportModal,
  onOpenWhatsAppModal,
  onOpenGoogleDriveModal,
  onOpenLogoModal,
  onOpenPaymentMethodsModal,
  onOpenExchangeRateModal,
  onOpenHomePopupModal,
  onOpenEmailMarketingModal,
  onOpenSmtpConfigModal,
  onOpenSearchConsoleModal,
  onOpenOnamaeDbModal,
  activeTab,
  setActiveTab,
  pendingShipmentsCount = 0
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  if (!isAdmin || !isLoggedIn) return null;

  return (
    <div className="fixed bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-[95vw] transition-all duration-300">
      
      {/* ----------------- EXPANDED PANEL STATE ----------------- */}
      {isExpanded ? (
        <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-700/80 shadow-2xl rounded-3xl p-4 sm:p-5 text-white w-[92vw] max-w-4xl max-h-[82vh] overflow-y-auto space-y-4 animate-scale-up border-t-2 border-t-amber-500/80">
          
          {/* Header Bar */}
          <div className="flex items-center justify-between pb-3 border-b border-slate-800">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-2xl bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center shrink-0 font-bold shadow-xs">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-black text-white">Painel de Gestão Administrador</h3>
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-400/30 text-[9px] font-extrabold uppercase font-mono tracking-wider">
                    Modo Suspenso
                  </span>
                </div>
                <p className="text-[11px] text-slate-400">
                  Acesso rápido a todas as ferramentas operacionais do depósito no Japão
                </p>
              </div>
            </div>

            <button
              onClick={() => setIsExpanded(false)}
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer flex items-center gap-1 text-xs font-bold"
              title="Recolher Painel Admin"
              id="admin-panel-close-btn"
            >
              <span>Minimizar</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          {/* Quick Action Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
            
            {/* NEW: 🎟️ Cupons de Desconto (Selo NOVO em destaque dourado/âmbar) */}
            {onOpenCouponsModal && (
              <button
                onClick={() => {
                  onOpenCouponsModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/90 hover:bg-slate-800 border-2 border-amber-500/70 hover:border-amber-400 bg-amber-950/40 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-lg shadow-amber-500/10 hover:scale-[1.02]"
                id="panel-btn-coupons-management"
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 font-black flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-md">
                  <Ticket className="w-5 h-5 text-slate-950" />
                </div>
                <div className="overflow-hidden flex-1">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-black text-amber-300 block truncate">Cupons de Desconto</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 font-black text-[9px] uppercase tracking-wider shadow-sm animate-pulse shrink-0">
                      NOVO
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Criar & gerenciar códigos promocionais
                  </span>
                </div>
              </button>
            )}

            {/* NEW: 🛒 Importar & Exportar Produtos Excel (Destaque verde e ícone de planilha) */}
            {onOpenExcelProductImportModal && (
              <button
                onClick={() => {
                  if (setActiveTab) setActiveTab('nossa-loja');
                  onOpenExcelProductImportModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/90 hover:bg-slate-800 border-2 border-emerald-500/70 hover:border-emerald-400 bg-emerald-950/40 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-lg shadow-emerald-500/10 hover:scale-[1.02]"
                id="panel-btn-excel-product-import"
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-slate-950 font-black flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-md">
                  <FileSpreadsheet className="w-5 h-5 text-slate-950" />
                </div>
                <div className="overflow-hidden flex-1">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-black text-emerald-300 block truncate">Importar & Exportar Excel</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 font-bold text-[9px] uppercase tracking-wider shrink-0">
                      📊 PLANILHA
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Importar & exportar produtos em massa
                  </span>
                </div>
              </button>
            )}

            {/* 0. Financeiro & Balanço */}
            {setActiveTab && (
              <button
                onClick={() => {
                  setActiveTab('admin-finances');
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-emerald-500/50 hover:border-emerald-400 bg-emerald-950/30 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-admin-finances-grid"
              >
                <div className="w-9 h-9 rounded-xl bg-emerald-500 text-slate-950 font-black flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-md">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-emerald-300 block flex items-center gap-1">
                    <span>Financeiro & Balanço 📊</span>
                  </span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Extratos, faturamento e receitas
                  </span>
                </div>
              </button>
            )}

            {/* Cotação de Câmbio (Manual ou Automática + Margem) */}
            {onOpenExchangeRateModal && (
              <button
                onClick={() => {
                  onOpenExchangeRateModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-gradient-to-br from-amber-950/40 via-slate-800 to-slate-800 hover:bg-slate-800 border border-amber-500/50 hover:border-amber-400 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-md relative"
                id="panel-btn-exchange-rate"
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 font-black flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-md">
                  <TrendingUp className="w-5 h-5 text-slate-950" />
                </div>
                <div className="overflow-hidden flex-1">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-black text-amber-300 block">Cotação de Câmbio 💱</span>
                    <span className="text-[9px] font-black px-1.5 py-0.2 bg-amber-400/20 text-amber-300 rounded-full border border-amber-400/30 uppercase">
                      Auto/%/Manual
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Valor fixo ou taxa automática com % a mais
                  </span>
                </div>
              </button>
            )}

            {/* 1. Entrada de Caixas */}
            {onOpenAdminEntry && (
              <button
                onClick={() => {
                  onOpenAdminEntry();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-amber-500/30 hover:border-amber-400 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-admin-entry"
              >
                <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <PackagePlus className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-amber-300 block">Entrada de Caixas</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Cadastrar novos pacotes no depósito
                  </span>
                </div>
              </button>
            )}

            {/* 1.1 Importar Pacotes em Massa */}
            {onOpenPackageExcelImport && (
              <button
                onClick={() => {
                  onOpenPackageExcelImport();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-emerald-500/40 hover:border-emerald-400 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-package-excel-import"
              >
                <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <FileSpreadsheet className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-emerald-300 block">Importar Pacotes (Excel/CSV)</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Cadastro em lote por planilha
                  </span>
                </div>
              </button>
            )}

            {/* 2. Lista de Clientes Cadastrados */}
            {setActiveTab && (
              <button
                onClick={() => {
                  setActiveTab('admin-clients');
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-emerald-500/40 hover:border-emerald-400 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-all-clients-list"
              >
                <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Users className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-emerald-300 block">Lista de Clientes (Suítes)</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Editar dados, celular & WhatsApp
                  </span>
                </div>
              </button>
            )}

            {/* 3. Importar Clientes / Suítes */}
            {onOpenClientImportModal && (
              <button
                onClick={() => {
                  onOpenClientImportModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-sky-500/30 hover:border-sky-400 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-client-import"
              >
                <div className="w-9 h-9 rounded-xl bg-sky-500/20 text-sky-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <UserCheck className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-sky-300 block truncate">Importador e Exportador de Clientes</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Importar & exportar lista de suítes
                  </span>
                </div>
              </button>
            )}

            {/* 3. Cadastrar Produtos Loja */}
            {onOpenAddStoreProductModal && (
              <button
                onClick={() => {
                  onOpenAddStoreProductModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-red-500/30 hover:border-red-400 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-add-store-product"
              >
                <div className="w-9 h-9 rounded-xl bg-red-500/20 text-red-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Store className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-red-300 block">Produtos da Loja</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Adicionar itens na vitrine JP
                  </span>
                </div>
              </button>
            )}

            {/* Atalho direto pra Loja */}
            {setActiveTab && (
              <button
                onClick={() => {
                  setActiveTab('nossa-loja');
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-red-500/50 hover:border-red-400 bg-red-950/20 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-go-to-store"
              >
                <div className="w-9 h-9 rounded-xl bg-red-500 text-white flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-md">
                  <Store className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-red-300 block flex items-center gap-1">
                    <span>Atalho pra Loja 🛍️</span>
                  </span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Ir direto para a Nossa Loja
                  </span>
                </div>
              </button>
            )}

            {/* 4. Pop-up Tela Inicial */}
            {onOpenHomePopupModal && (
              <button
                onClick={() => {
                  onOpenHomePopupModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-purple-500/30 hover:border-purple-400 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-home-popup"
              >
                <div className="w-9 h-9 rounded-xl bg-purple-500/20 text-purple-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Megaphone className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-purple-300 block">Pop-up Tela Inicial</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Ativar e enviar anúncios
                  </span>
                </div>
              </button>
            )}

            {/* Disparo E-mail Marketing */}
            {onOpenEmailMarketingModal && (
              <button
                onClick={() => {
                  onOpenEmailMarketingModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-amber-500/40 hover:border-amber-400 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-email-marketing"
              >
                <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-amber-300 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Mail className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-amber-200 block">E-mail Marketing ✉️</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Disparo em massa & boletins
                  </span>
                </div>
              </button>
            )}

            {/* Configurar E-mail do Website (SMTP) */}
            {onOpenSmtpConfigModal && (
              <button
                onClick={() => {
                  onOpenSmtpConfigModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-sky-500/40 hover:border-sky-400 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-smtp-config"
              >
                <div className="w-9 h-9 rounded-xl bg-sky-500/20 text-sky-300 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Server className="w-5 h-5 text-sky-300" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-sky-200 block">Configurar E-mail (SMTP) 📧</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Dados de envio do website
                  </span>
                </div>
              </button>
            )}

            {/* Onamae.com MySQL Database Manager */}
            {onOpenOnamaeDbModal && (
              <button
                onClick={() => {
                  onOpenOnamaeDbModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-red-500/50 hover:border-red-400 bg-red-950/20 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-onamae-db"
              >
                <div className="w-9 h-9 rounded-xl bg-red-600 text-white font-black flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-md relative">
                  <Database className="w-5 h-5 text-white" />
                  <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-emerald-400 border border-slate-900 animate-pulse" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-red-300 block flex items-center gap-1.5">
                    <span>Central de Banco & Backup 🗄️</span>
                    <span className="text-[9px] font-bold px-1.5 py-0.2 bg-emerald-500/20 text-emerald-300 rounded border border-emerald-500/30">
                      Switches + Backup
                    </span>
                  </span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Seletor de banco exclusivo, backup PC, Master Reset e Onamae MySQL
                  </span>
                </div>
              </button>
            )}

            {/* Google Search Console & SEO */}
            {onOpenSearchConsoleModal && (
              <button
                onClick={() => {
                  onOpenSearchConsoleModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-sky-400/50 hover:border-sky-300 bg-sky-950/30 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-search-console"
              >
                <div className="w-9 h-9 rounded-xl bg-sky-500 text-slate-950 font-black flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-md">
                  <Search className="w-5 h-5 text-slate-950" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-sky-300 block flex items-center gap-1">
                    <span>Google Search Console 🔍</span>
                  </span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    SEO, Sitemap e verificação Google
                  </span>
                </div>
              </button>
            )}

            {/* Histórico de E-mails Enviados */}
            {setActiveTab && (
              <button
                onClick={() => {
                  setActiveTab('admin-email-history');
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-emerald-500/40 hover:border-emerald-400 bg-emerald-950/20 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-email-history"
              >
                <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Inbox className="w-5 h-5 text-emerald-300" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-emerald-200 block">Histórico de E-mails 📥</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Status de entrega das confirmações
                  </span>
                </div>
              </button>
            )}

            {/* 5. WhatsApp API Meta */}
            {onOpenWhatsAppModal && (
              <button
                onClick={() => {
                  onOpenWhatsAppModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-emerald-500/30 hover:border-emerald-400 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-whatsapp"
              >
                <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-emerald-300 block">WhatsApp API Meta</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Notificações automáticas
                  </span>
                </div>
              </button>
            )}

            {/* 6. Formas de Pagamento */}
            {onOpenPaymentMethodsModal && (
              <button
                onClick={() => {
                  onOpenPaymentMethodsModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-amber-400/30 hover:border-amber-300 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-payment-methods"
              >
                <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-amber-300 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-amber-200 block">Formas de Pagamento</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    PIX, Carteiras, Cartões
                  </span>
                </div>
              </button>
            )}

            {/* 7. Personalizar Marca */}
            {onOpenLogoModal && (
              <button
                onClick={() => {
                  onOpenLogoModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-sky-500/30 hover:border-sky-400 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-logo-brand"
              >
                <div className="w-9 h-9 rounded-xl bg-sky-500/20 text-sky-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Upload className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-sky-300 block">Personalizar Marca</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Logotipo e Favicon
                  </span>
                </div>
              </button>
            )}

            {/* 8. Google Drive */}
            {onOpenGoogleDriveModal && (
              <button
                onClick={() => {
                  onOpenGoogleDriveModal();
                  setIsExpanded(false);
                }}
                className="p-3 bg-slate-800/80 hover:bg-slate-800 border border-blue-500/30 hover:border-blue-400 rounded-2xl text-left transition-all cursor-pointer group flex items-start gap-3 shadow-xs"
                id="panel-btn-google-drive"
              >
                <div className="w-9 h-9 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <HardDrive className="w-5 h-5" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs font-black text-blue-300 block">Google Drive</span>
                  <span className="text-[10px] text-slate-400 block leading-tight mt-0.5">
                    Backup e arquivos
                  </span>
                </div>
              </button>
            )}

          </div>

          {/* Quick Overview Tabs Bar */}
          {setActiveTab && (
            <div className="pt-3 border-t border-slate-800 flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <Sliders className="w-3.5 h-3.5 text-amber-400" />
                <span>Navegação Rápida Admin:</span>
              </span>

              <div className="flex items-center gap-2 overflow-x-auto py-1">
                <button
                  onClick={() => {
                    setActiveTab('admin-finances');
                    setIsExpanded(false);
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                    activeTab === 'admin-finances'
                      ? 'bg-emerald-500 text-slate-950 font-black shadow-md'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                  id="panel-btn-admin-finances"
                >
                  <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Financeiro & Balanço 📊</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('nossa-loja');
                    setIsExpanded(false);
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                    activeTab === 'nossa-loja'
                      ? 'bg-red-500 text-white font-black shadow-md'
                      : 'bg-slate-800 text-red-300 hover:bg-slate-700'
                  }`}
                  id="panel-btn-admin-loja-shortcut"
                >
                  <Store className="w-3.5 h-3.5 text-red-400" />
                  <span>Atalho pra Loja</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('admin-packages');
                    setIsExpanded(false);
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                    activeTab === 'admin-packages'
                      ? 'bg-amber-500 text-slate-950 font-black shadow-md'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <Box className="w-3.5 h-3.5" />
                  <span>Estoque Geral</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('admin-shipments');
                    setIsExpanded(false);
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center justify-between gap-1.5 transition-all cursor-pointer ${
                    activeTab === 'admin-shipments'
                      ? 'bg-sky-500 text-slate-950 font-black shadow-md'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5" />
                    <span>Envios & Pedidos</span>
                  </span>
                  {pendingShipmentsCount > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] font-black bg-rose-500 text-white rounded-full animate-pulse shadow-sm">
                      {pendingShipmentsCount} em espera
                    </span>
                  )}
                </button>

                <button
                  onClick={() => {
                    setActiveTab('admin-clients');
                    setIsExpanded(false);
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                    activeTab === 'admin-clients'
                      ? 'bg-indigo-500 text-slate-950 font-black shadow-md'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <Users className="w-3.5 h-3.5" />
                  <span>Suítes de Clientes</span>
                </button>

                <button
                  onClick={() => {
                    setActiveTab('admin-email-history');
                    setIsExpanded(false);
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                    activeTab === 'admin-email-history'
                      ? 'bg-emerald-500 text-slate-950 font-black shadow-md'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                  id="panel-btn-tab-email-history"
                >
                  <Inbox className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Histórico de E-mails 📥</span>
                </button>
              </div>
            </div>
          )}

        </div>
      ) : (
        /* ----------------- COLLAPSED FLOATING DOCK STATE ----------------- */
        <div className="bg-slate-900/90 hover:bg-slate-900 backdrop-blur-md text-white border border-slate-700/80 shadow-2xl rounded-full p-1.5 sm:p-2 flex items-center gap-1.5 sm:gap-2 animate-fade-in border-t-2 border-t-amber-500 overflow-x-auto max-w-[92vw] whitespace-nowrap">
          
          {/* Main Toggle Button */}
          <button
            onClick={() => setIsExpanded(true)}
            className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black text-xs rounded-full flex items-center gap-1.5 shadow-md transition-all cursor-pointer group"
            title="Abrir Painel Suspenso de Gestão"
            id="admin-dock-expand-btn"
          >
            <Shield className="w-4 h-4 text-slate-950 shrink-0" />
            <span className="hidden sm:inline">Painel Admin</span>
            <ChevronUp className="w-4 h-4 text-slate-950 group-hover:-translate-y-0.5 transition-transform" />
          </button>

          <div className="h-5 w-px bg-slate-700 my-auto" />

          {/* Quick Floating Action Icons */}
          {onOpenCouponsModal && (
            <button
              onClick={onOpenCouponsModal}
              className="p-2 bg-gradient-to-br from-amber-400/30 to-amber-600/30 hover:from-amber-400/50 hover:to-amber-600/50 text-amber-300 rounded-full transition-all cursor-pointer hover:scale-110 border border-amber-400/50 relative shadow-sm"
              title="🎟️ Cupons de Desconto (NOVO)"
              id="dock-btn-coupons"
            >
              <Ticket className="w-4 h-4 text-amber-300" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full animate-ping" />
            </button>
          )}

          {onOpenExcelProductImportModal && (
            <button
              onClick={() => {
                if (setActiveTab) setActiveTab('nossa-loja');
                onOpenExcelProductImportModal();
              }}
              className="p-2 bg-gradient-to-br from-emerald-400/30 to-emerald-600/30 hover:from-emerald-400/50 hover:to-emerald-600/50 text-emerald-300 rounded-full transition-all cursor-pointer hover:scale-110 border border-emerald-400/50 shadow-sm"
              title="📊 Importar & Exportar Produtos Excel"
              id="dock-btn-excel-import"
            >
              <FileSpreadsheet className="w-4 h-4 text-emerald-300" />
            </button>
          )}

          {setActiveTab && (
            <button
              onClick={() => setActiveTab('admin-shipments')}
              className="relative p-2 bg-sky-500/20 hover:bg-sky-500/40 text-sky-300 rounded-full transition-all cursor-pointer hover:scale-105 border border-sky-500/30"
              title={`Pedidos de Envio (${pendingShipmentsCount} em espera)`}
              id="dock-btn-shipments"
            >
              <Truck className="w-4 h-4 text-sky-400" />
              {pendingShipmentsCount > 0 && (
                <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[9px] font-black bg-rose-500 text-white rounded-full animate-pulse border border-slate-900 shadow-xs">
                  {pendingShipmentsCount}
                </span>
              )}
            </button>
          )}

          {setActiveTab && (
            <button
              onClick={() => setActiveTab('admin-finances')}
              className="p-2 bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-300 rounded-full transition-all cursor-pointer hover:scale-105 border border-emerald-500/30"
              title="Financeiro & Balanço 📊"
              id="dock-btn-finances"
            >
              <DollarSign className="w-4 h-4 text-emerald-400" />
            </button>
          )}

          {onOpenExchangeRateModal && (
            <button
              onClick={onOpenExchangeRateModal}
              className="p-2 bg-gradient-to-br from-amber-400/20 to-amber-600/20 hover:from-amber-400/40 hover:to-amber-600/40 text-amber-300 rounded-full transition-all cursor-pointer hover:scale-105 border border-amber-400/40"
              title="💱 Cotação de Câmbio (Manual ou Automática + %)"
              id="dock-btn-exchange-rates"
            >
              <TrendingUp className="w-4 h-4 text-amber-300" />
            </button>
          )}

          {setActiveTab && (
            <button
              onClick={() => setActiveTab('nossa-loja')}
              className="p-2 bg-red-500/20 hover:bg-red-500/40 text-red-300 rounded-full transition-all cursor-pointer hover:scale-105 border border-red-500/30"
              title="Atalho pra Loja (Nossa Loja)"
              id="dock-btn-go-to-store"
            >
              <Store className="w-4 h-4" />
            </button>
          )}

          {setActiveTab && (
            <button
              onClick={() => setActiveTab('admin-clients')}
              className="p-2 bg-emerald-500/10 hover:bg-emerald-500/30 text-emerald-400 rounded-full transition-all cursor-pointer hover:scale-105"
              title="Lista de Clientes Cadastrados"
              id="dock-btn-all-clients"
            >
              <Users className="w-4 h-4" />
            </button>
          )}

          {onOpenAdminEntry && (
            <button
              onClick={onOpenAdminEntry}
              className="p-2 bg-amber-500/10 hover:bg-amber-500/30 text-amber-400 rounded-full transition-all cursor-pointer hover:scale-105"
              title="Entrada de Caixas"
              id="dock-btn-entry"
            >
              <PackagePlus className="w-4 h-4" />
            </button>
          )}

          {onOpenAddStoreProductModal && (
            <button
              onClick={onOpenAddStoreProductModal}
              className="p-2 bg-red-500/10 hover:bg-red-500/30 text-red-400 rounded-full transition-all cursor-pointer hover:scale-105"
              title="Cadastrar Produtos Loja"
              id="dock-btn-store"
            >
              <Store className="w-4 h-4" />
            </button>
          )}

          {onOpenHomePopupModal && (
            <button
              onClick={onOpenHomePopupModal}
              className="p-2 bg-purple-500/10 hover:bg-purple-500/30 text-purple-400 rounded-full transition-all cursor-pointer hover:scale-105"
              title="Pop-up Tela Inicial"
              id="dock-btn-popup"
            >
              <Megaphone className="w-4 h-4" />
            </button>
          )}

          {onOpenEmailMarketingModal && (
            <button
              onClick={onOpenEmailMarketingModal}
              className="p-2 bg-amber-500/10 hover:bg-amber-500/30 text-amber-300 rounded-full transition-all cursor-pointer hover:scale-105"
              title="Disparo E-mail Marketing"
              id="dock-btn-email-marketing"
            >
              <Mail className="w-4 h-4" />
            </button>
          )}

          {onOpenWhatsAppModal && (
            <button
              onClick={onOpenWhatsAppModal}
              className="p-2 bg-emerald-500/10 hover:bg-emerald-500/30 text-emerald-400 rounded-full transition-all cursor-pointer hover:scale-105"
              title="WhatsApp API Meta"
              id="dock-btn-whatsapp"
            >
              <MessageSquare className="w-4 h-4" />
            </button>
          )}

          {onOpenPaymentMethodsModal && (
            <button
              onClick={onOpenPaymentMethodsModal}
              className="p-2 bg-amber-500/10 hover:bg-amber-500/30 text-amber-300 rounded-full transition-all cursor-pointer hover:scale-105"
              title="Formas de Pagamento"
              id="dock-btn-payments"
            >
              <CreditCard className="w-4 h-4" />
            </button>
          )}

        </div>
      )}

    </div>
  );
};

export default AdminSidebar;
